// Pieces Operator › Deploys shares (DC-04): the state badge, labels, what each failure code
// means, and the hand-off timeline (ADR-0018 §5: staged → approved → handed_off → confirmed |
// failed; staged → rejected; a redeploy or rollback skips the approval).
import { Badge, cn } from "@companyos/ui";
import { CircleAlertIcon, CircleCheckIcon, CircleDashedIcon, CircleDotIcon } from "lucide-react";
import type { Deploy, MeResponse } from "../../../api/types";
import { formatDateTime } from "../../format";

type Variant = "neutral" | "info" | "success" | "warning" | "danger" | "outline";

const STATE: Record<Deploy["state"], { label: string; variant: Variant }> = {
  staged: { label: "Staged", variant: "info" },
  approved: { label: "Approved", variant: "info" },
  handed_off: { label: "Handed off", variant: "warning" },
  confirmed: { label: "Confirmed", variant: "success" },
  failed: { label: "Failed", variant: "danger" },
  rejected: { label: "Rejected", variant: "neutral" },
};

export function DeployStateBadge({
  deploy,
}: {
  deploy: Pick<Deploy, "state" | "path" | "kind" | "bundle_id">;
}) {
  const s = STATE[deploy.state];
  const override = awaitsOverride(deploy);
  const waiting = awaitsApproval(deploy);
  return (
    <Badge variant={override || waiting ? "warning" : s.variant} dot data-state={deploy.state}>
      {override ? "Awaiting override" : waiting ? "Awaiting approval" : s.label}
    </Badge>
  );
}

/**
 * DC-05: a staged deploy of a version with no verification skill, waiting for an operator's
 * verification override (it has no bundle until then).
 */
export function awaitsOverride(d: Pick<Deploy, "state" | "bundle_id">): boolean {
  return d.state === "staged" && d.bundle_id === null;
}

export const KIND_LABEL: Record<Deploy["kind"], string> = {
  first: "First deploy",
  redeploy: "Redeploy",
  rollback: "Rollback",
};

/** A `done_for_you` first deploy still waiting for a person. */
export function awaitsApproval(d: Pick<Deploy, "state" | "path" | "kind">): boolean {
  return d.state === "staged" && d.path === "done_for_you" && d.kind === "first";
}

/** What each failure code means for the operator (ADR-0018 §4 plus CompanyOS's own). */
export const ERROR_HELP: Record<NonNullable<Deploy["error"]>["code"], string> = {
  bundle_sha_mismatch: "AiGentPanel computed a different bundle hash than CompanyOS sent.",
  bundle_rejected: "AiGentPanel refused the bundle's contents.",
  node_unreachable: "AiGentPanel could not reach a node to run the DE.",
  health_check_failed: "The DE's container started but did not pass its health check.",
  container_company_mismatch: "The container answered for another company.",
  intake_unreachable: "AiGentPanel's intake did not answer after every retry.",
  intake_unauthorized: "AiGentPanel refused CompanyOS's credentials for its intake.",
  handoff_timeout: "AiGentPanel accepted the deploy but never confirmed it within 30 minutes.",
  override_expired: "The pilot's verification override ended before the deploy was handed off.",
  internal: "CompanyOS could not hand the deploy off.",
  budget_unsupported:
    "The seat budget cannot become the DE's LiteLLM key budget (it needs money, in USD).",
};

/** `Acme Recruiting`, or a short id when the company is not among the caller's grants. */
export function companyName(me: MeResponse, id: string): string {
  return me.agency?.grants.find((g) => g.company_id === id)?.company_name ?? `…${id.slice(-6)}`;
}

type StepState = "done" | "current" | "pending" | "failed" | "skipped";
type Step = {
  key: string;
  label: string;
  state: StepState;
  at: string | null;
  note?: string | undefined;
};

/** The deploy's steps with what each one is now. */
export function timelineOf(d: Deploy): Step[] {
  const needsApproval = d.path === "done_for_you" && d.kind === "first";
  const steps: Step[] = [{ key: "staged", label: "Staged", state: "done", at: d.created_at }];
  if (d.state === "rejected") {
    steps.push({
      key: "rejected",
      label: "Rejected",
      state: "failed",
      at: d.updated_at,
      note: d.rejected_reason ?? undefined,
    });
    return steps;
  }
  const afterApproval = ["approved", "handed_off", "confirmed", "failed"].includes(d.state);
  steps.push(
    needsApproval
      ? {
          key: "approved",
          label: "Approved",
          state: d.approved_at ? "done" : d.state === "staged" ? "current" : "skipped",
          at: d.approved_at,
          note: d.approved_at
            ? undefined
            : awaitsOverride(d)
              ? "Waiting for an operator's verification override, then a person's approval."
              : "Waiting for a person to approve this first deploy.",
        }
      : {
          key: "approved",
          label: "Approval not needed",
          state: "skipped",
          at: null,
          note: "This version is already confirmed in the company.",
        },
  );
  const handedOff = d.handed_off_at !== null;
  const handOffState: StepState = handedOff
    ? "done"
    : d.state === "failed"
      ? "failed"
      : afterApproval || !needsApproval
        ? "current"
        : "pending";
  steps.push({
    key: "handed_off",
    label: "Handed off to AiGentPanel",
    state: handOffState,
    at: d.handed_off_at,
    note:
      d.handoff_attempts > 1
        ? `${d.handoff_attempts} attempts`
        : handOffState === "current"
          ? "The hand-off worker posts the bundle to AiGentPanel's intake."
          : undefined,
  });
  if (d.state === "failed")
    steps.push({
      key: "failed",
      label: "Failed",
      state: "failed",
      at: d.failed_at,
      note: d.error ? `${d.error.code}: ${d.error.message}` : undefined,
    });
  else
    steps.push({
      key: "confirmed",
      label: "Confirmed by AiGentPanel",
      state: d.state === "confirmed" ? "done" : d.state === "handed_off" ? "current" : "pending",
      at: d.confirmed_at,
      note: d.state === "handed_off" ? "Waiting for the panel's confirmation." : undefined,
    });
  return steps;
}

const STEP_ICON = {
  done: CircleCheckIcon,
  current: CircleDotIcon,
  pending: CircleDashedIcon,
  skipped: CircleDashedIcon,
  failed: CircleAlertIcon,
} as const;
const STEP_TONE: Record<StepState, string> = {
  done: "text-success-text",
  current: "text-info-text",
  pending: "text-muted-foreground",
  skipped: "text-muted-foreground",
  failed: "text-danger-text",
};
const STEP_WORD: Record<StepState, string> = {
  done: "done",
  current: "in progress",
  pending: "not yet",
  skipped: "skipped",
  failed: "failed",
};

/** The hand-off status timeline: one row per step, its time, and a note. */
export function DeployTimeline({ deploy }: { deploy: Deploy }) {
  const steps = timelineOf(deploy);
  return (
    <ol data-slot="deploy-timeline" className="flex flex-col gap-3">
      {steps.map((s) => {
        const Icon = STEP_ICON[s.state];
        return (
          <li key={s.key} data-step={s.key} data-step-state={s.state} className="flex gap-3">
            <Icon aria-hidden="true" className={cn("mt-0.5 size-4 shrink-0", STEP_TONE[s.state])} />
            <div className="flex min-w-0 flex-col gap-0.5">
              <p className="text-sm font-medium">
                {s.label}
                <span className="sr-only"> ({STEP_WORD[s.state]})</span>
              </p>
              {s.at ? (
                <p className="text-xs text-muted-foreground">{formatDateTime(s.at)}</p>
              ) : null}
              {s.note ? (
                <p className="text-sm text-muted-foreground break-words">{s.note}</p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
